import {
    Box,
    Button,
    SimpleGrid,
    useDisclosure,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    useRadioGroup,
    Flex,
    Heading,
    Text,
    Divider,
    Select,
    RadioGroup,
    Radio,
    Stack,
    Alert,
    AlertIcon
} from '@chakra-ui/react'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import ScanningRadioCard from './components/ScanningRadioCard'
import {
    BrowserMultiFormatReader,
    ChecksumException,
    FormatException
} from '@zxing/library'
import CheckInModal from './components/CheckInModal'
import { getFromLocalStorage } from '@/shared/utils/localStorageHelper'
import { BranchType } from '@/shared/api/hooks/useBranches'
import CheckOutModal from './components/CheckOutModal'
import { useAlertService } from '@/shared/lib/useAlertService'
import { PROTECTED_ROUTES } from '@/shared/config/routes'
import { ProtectedPage } from '@/shared/components/ProtectedPage'
import BranchDropdown from '@/components/BranchDropdown'
import { SERVICE_SESSION } from './types'

const SESSION_OPTIONS = [
    { value: SERVICE_SESSION.MORNING, label: 'Morning' },
    { value: SERVICE_SESSION.EVENING, label: 'Evening' }
]

// disable because of eslint false positive
// reverted to original state
/* eslint-disable */
enum Check {
    Null = 'Null',
    In = 'Check In',
    Out = 'Check Out'
}
/* eslint-enable */
const options = [Check.In, Check.Out]

const CheckInOut = () => {
    const [check, setCheck] = useState<string>(Check.Null)
    const [qrCode, setQrCode] = useState<string | null>(null)
    const [session, setSession] = useState<string>('')
    const currentBranch = getFromLocalStorage<BranchType>('currentBranch')
    const alertService = useAlertService()

    const videoRef = useRef<HTMLVideoElement>()
    const codeReader = useRef(new BrowserMultiFormatReader())

    const [videoInputDevices, setVideoInputDevices] = useState<MediaDeviceInfo[]>([])
    const [selectedDeviceId, setSelectedDeviceId] = useState('')

    // set up radio cards for check-in/out actions
    const { getRootProps, getRadioProps, setValue } = useRadioGroup({
        name: 'check',
        defaultValue: Check.Null,
        onChange: setCheck
    })
    const group = getRootProps()

    const clearImage = () => {
        setQrCode(null)
    }

    // modal control & handlers
    const {
        isOpen: scannerIsOpen,
        onOpen: scannerOnOpen,
        onClose: scannerOnClose
    } = useDisclosure({ onClose: clearImage })

    const {
        isOpen: checkinModalIsOpen,
        onOpen: checkinModalOnOpen,
        onClose: checkinModalOnClose
    } = useDisclosure()

    const {
        isOpen: checkoutModalIsOpen,
        onOpen: checkoutModalOnOpen,
        onClose: checkoutModalOnClose
    } = useDisclosure()

    const handleScanner = (result: string) => {
        setQrCode(result)
        if (check === Check.In) {
            checkinModalOnOpen()
        } else {
            checkoutModalOnOpen()
        }
    }

    const handleCheckinModalClose = () => {
        codeReader.current.reset()
        checkinModalOnClose()
        scannerOnClose()
        setValue(Check.Null) // reset the radio button group
        setCheck(Check.Null) // disable scan button
        setSession('') // reset session selection
    }

    const handleCheckoutModalClose = () => {
        codeReader.current.reset()
        checkoutModalOnClose()
        scannerOnClose()
        setValue(Check.Null) // reset the radio button group
        setCheck(Check.Null) // disable scan button
        setSession('') // reset session selection
    }

    const handleScannerModalClose = async () => {
        codeReader.current.reset()
        scannerOnClose()
    }

    const handleInputDeviceChange = (event: ChangeEvent<HTMLSelectElement>) => {
        setSelectedDeviceId(event.target.value)
    }

    const startScanner = async () => {
        try {
            const videoInputs = await codeReader.current.listVideoInputDevices()
            setVideoInputDevices(videoInputs)
            const deviceId =
                selectedDeviceId ||
                (videoInputs.length > 1
                    ? videoInputs[1].deviceId
                    : videoInputs[0].deviceId)
            setSelectedDeviceId(deviceId)

            const result = await codeReader.current.decodeOnceFromVideoDevice(
                selectedDeviceId,
                videoRef.current
            )

            handleScanner(result.getText())
            codeReader.current.reset()
        } catch (error) {
            if (error instanceof ChecksumException) {
                alertService.error("A code was found, but it's read value was not valid.")
            }

            if (error instanceof FormatException) {
                alertService.error('A code was found, but it was in a invalid format.')
            }
        }
    }

    const handleClick = () => {
        scannerOnOpen()
        startScanner()
    }

    useEffect(() => {}, [check])

    useEffect(() => {
        // To ensure the correct camera is used when switching devices
        // trigger the startScanner() whenever the 'selectedDeviceId' changes.
        if (selectedDeviceId) {
            startScanner()
        }
    }, [selectedDeviceId])

    return (
        <ProtectedPage routeConfig={PROTECTED_ROUTES['/user/teacher/check-in-out']}>
            <Box
                minH={['container.sm', 'container.md']}
                borderRadius={4}
                mx={'auto'}
                py={5}
                px={{ base: 2, md: 17 }}
            >
                <Flex my={2} alignItems="center" gap="2" flexWrap="wrap">
                    <Box>
                        <Heading size="md" my={2}>
                            Check In/Check out Child
                        </Heading>
                        <Text color={'gray.600'}>
                            Here you can check in or check out a child
                        </Text>
                    </Box>
                </Flex>
                <Divider />

                <BranchDropdown />

                <Heading fontSize="medium" mt={6} mb={4}>
                    Choose Actions
                </Heading>
                <SimpleGrid
                    columns={2}
                    spacing={4}
                    templateColumns="repeat(auto-fill, minmax(300px, 1fr))"
                    {...group}
                >
                    {options.map((value) => {
                        const radio = getRadioProps({ value })
                        return (
                            <ScanningRadioCard key={value} {...radio}>
                                {value}
                            </ScanningRadioCard>
                        )
                    })}
                </SimpleGrid>

                <Box mt={6}>
                    <Heading fontSize="medium" mb={2}>
                        Service Session{' '}
                        <Text as="span" color="red.500">
                            *
                        </Text>
                    </Heading>
                    <RadioGroup onChange={setSession} value={session}>
                        <Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
                            {SESSION_OPTIONS.map(({ value, label }) => {
                                // A bare Radio on white was very hard to spot, so
                                // each option is a selectable card using the same
                                // brand treatment as the Check In / Check Out cards
                                // above. The wrapper is a Box, not a label, because
                                // Radio renders its own label and nesting them is
                                // invalid markup.
                                const isSelected = session === value
                                return (
                                    <Box
                                        key={value}
                                        onClick={() => setSession(value)}
                                        cursor="pointer"
                                        px={4}
                                        py={3}
                                        minW={{ base: 'full', sm: '150px' }}
                                        borderRadius="md"
                                        borderWidth={isSelected ? '2px' : '1px'}
                                        borderColor={
                                            isSelected ? 'brand.600' : 'gray.300'
                                        }
                                        bg={isSelected ? 'brand.50' : 'white'}
                                        _hover={{
                                            borderColor: isSelected
                                                ? 'brand.600'
                                                : 'gray.400'
                                        }}
                                    >
                                        <Radio
                                            size="lg"
                                            colorScheme="brand"
                                            borderColor="gray.400"
                                            value={value}
                                            fontWeight={
                                                isSelected ? 'semibold' : 'normal'
                                            }
                                        >
                                            {label}
                                        </Radio>
                                    </Box>
                                )
                            })}
                        </Stack>
                    </RadioGroup>
                </Box>

                <Alert
                    status="info"
                    variant="subtle"
                    borderRadius={'md'}
                    marginY="4"
                    width="sm"
                >
                    <AlertIcon />
                    Please ensure you select same session you used in checking-in the
                    child.
                </Alert>

                <Button
                    marginTop="4"
                    isDisabled={Check.Null === check || !currentBranch || !session}
                    onClick={handleClick}
                >
                    Scan QR Code
                </Button>
                {/* Begin of scanning modal logic */}

                {/* Scanner camera modal */}
                <Modal
                    isOpen={scannerIsOpen}
                    onClose={handleScannerModalClose}
                    size="full"
                    scrollBehavior="outside"
                    blockScrollOnMount={false}
                >
                    <ModalOverlay />
                    <ModalContent bg="brand.700" color="#fff">
                        <ModalHeader textAlign="center">
                            Scan QR Code for{' '}
                            {check === Check.In ? ' Check In' : 'Check Out'}
                        </ModalHeader>
                        <ModalCloseButton size="lg" />

                        <ModalBody>
                            <Box
                                height="calc(100vh - 132px)"
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <Box
                                    width="min(80vw, 80vh)"
                                    aspectRatio={'1 / 1'}
                                    height="auto"
                                    border="2px white solid"
                                    borderRadius={4}
                                    position="relative"
                                >
                                    <video
                                        // @ts-expect-error video element ref typing
                                        ref={videoRef}
                                        id="video"
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </Box>
                                <Box maxW="sm" my={4}>
                                    <Select
                                        onChange={(event) =>
                                            handleInputDeviceChange(event)
                                        }
                                        placeholder="Change camera"
                                    >
                                        {videoInputDevices.map((device) => (
                                            <option
                                                key={device.deviceId}
                                                value={device.deviceId}
                                            >
                                                {device.label}
                                            </option>
                                        ))}
                                    </Select>
                                </Box>
                            </Box>
                        </ModalBody>
                    </ModalContent>
                </Modal>

                {/* Parent modal. Each modal is gated on its own isOpen as well
                    as the scan: both used to mount together on any scan, so the
                    child lookup ran twice and a bad QR raised two error toasts
                    and two close handlers. */}
                {qrCode !== null && checkinModalIsOpen ? (
                    <CheckInModal
                        qrCode={qrCode}
                        isOpen={checkinModalIsOpen}
                        onClose={handleCheckinModalClose}
                        session={session}
                    />
                ) : null}

                {qrCode !== null && checkoutModalIsOpen ? (
                    <CheckOutModal
                        qrCode={qrCode}
                        isOpen={checkoutModalIsOpen}
                        onClose={handleCheckoutModalClose}
                        session={session}
                    />
                ) : null}
            </Box>
        </ProtectedPage>
    )
}

export default CheckInOut
